import { Badge, Button } from '@auctisync/ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { HouseSelector } from '../../components/HouseSelector';
import { useOwnedTenants } from '../../hooks/useOwnedTenants';
import { useSession } from '../../hooks/useSession';
import { invokeEF } from '../../lib/edgeFunctions';
import { supabase } from '../../lib/supabase';
import {
  buildDoneMap,
  buildTitleMap,
  computeProgress,
  isStepDone,
  isStepLocked,
  unmetDependencies,
  type OnboardingKind,
  type OnboardingStateRow,
  type OnboardingStatus,
} from './onboardingChecklist';

/** Mirrors the verify-custom-domain EF response (DomainVerification). */
type DomainCheck = {
  verified: boolean;
  domain: string;
  dns: {
    cname: string | null;
    a_records: string[];
    aaaa_records: string[];
    points_at_auctisync: boolean;
  };
  tls: { reachable: boolean; status: number | null };
  reason?: 'dns_not_pointed' | 'apex_needs_cname' | 'tls_not_ready' | 'unresolved';
};

/** Plain-English "what's still missing" for a not-yet-verified domain check. */
function domainNotReadyCopy(c: DomainCheck): string {
  switch (c.reason) {
    case 'unresolved':
      return `We couldn’t find any DNS records for ${c.domain} yet. Add the record at your registrar — changes can take a little while to spread.`;
    case 'apex_needs_cname':
      return `${c.domain} looks like a root domain. Point it at AuctiSync with a CNAME to auctisync.pages.dev — a subdomain such as bids.${c.domain} is easiest — then check again.`;
    case 'dns_not_pointed':
      return `${c.domain} resolves, but it isn’t pointed at AuctiSync yet. Point it at us with a CNAME to auctisync.pages.dev, then check again.`;
    case 'tls_not_ready':
      return `${c.domain} is pointed at us — we’re still issuing the secure certificate. This usually finishes within a few minutes; check again shortly.`;
    default:
      return `${c.domain} isn’t ready yet. Check again in a few minutes.`;
  }
}

/** Friendly copy for an EF/transport error from verify-custom-domain. */
function domainErrorCopy(code: string): string {
  switch (code) {
    case 'no_custom_domain':
      return 'No custom domain is set yet — choose one with your setup assistant first.';
    case 'invalid_custom_domain':
      return 'The saved domain looks malformed — re-enter it with your setup assistant.';
    case 'not_a_tenant_owner':
    case 'not_tenant_owner':
      return 'Only the auction house owner can verify the domain.';
    default:
      return 'We couldn’t run the domain check just now. Please try again in a moment.';
  }
}

/**
 * Tenant admin — Onboarding checklist (spec §2/§3).
 *
 * A PURE PROGRESS MIRROR of the onboarding state machine — NOT a form wizard.
 * The copilot does the heavy lifting (later chunks); this surface only
 * reflects `get_onboarding_state`, which LEFT-JOINs the step registry to this
 * tenant's progress and computes a per-step tier `entitled` flag (§10).
 *
 * It surfaces only while the tenant is in `publish_state='draft'` (the nav link
 * is gated the same way in AdminNav), so live tenants never see a ghost
 * checklist. Once go-live flips the tenant to 'live', this reads as complete.
 *
 * Read-only by design: there are no inputs here. Steps advance through the
 * copilot (copilot-kind), Stripe/DNS webhooks (external-kind), or the owner's
 * own review/go-live actions (manual-kind) — each writing the backbone via
 * set_onboarding_step_status on the server.
 */

/**
 * Typed bridge for get_onboarding_state until `database.ts` regenerates
 * centrally (same idiom as AdminAiSettings' set_ai_settings bridge) — keeps
 * the call fully typed without widening the shared client.
 */
type OnboardingRpc = (
  fn: 'get_onboarding_state',
  args: { p_tenant_id: string },
) => Promise<{ data: OnboardingStateRow[] | null; error: { message: string } | null }>;
const onboardingRpc = supabase.rpc.bind(supabase) as unknown as OnboardingRpc;

type PublishResult = { published?: boolean; already_live?: boolean; blocked_by?: string[] };
type PublishRpc = (
  fn: 'publish_tenant',
  args: { p_tenant_id: string },
) => Promise<{ data: PublishResult | null; error: { message: string } | null }>;
const publishRpc = supabase.rpc.bind(supabase) as unknown as PublishRpc;

/** Response from the materialize-onboarding-bidders EF (one batch). */
type MaterializeResponse = {
  materialized?: number;
  created_users?: number;
  reused_users?: number;
  invited?: number;
  failed?: number;
  pending?: number; // actionable rows still staged (attempts < 5)
  exhausted?: number; // parked rows (attempts >= 5) needing manual attention
  done?: boolean; // empty claimed batch → nothing left to drain
  error?: string;
};

type StatusMeta = { label: string; symbol: string; color: string; bg: string };

const STATUS_META: Record<OnboardingStatus, StatusMeta> = {
  complete: {
    label: 'Done',
    symbol: '✓',
    color: 'var(--semantic-success)',
    bg: 'color-mix(in oklab, var(--semantic-success) 15%, transparent)',
  },
  in_progress: {
    label: 'In progress',
    symbol: '●',
    color: 'var(--brand-primary-fg, #ffffff)',
    bg: 'var(--brand-primary, #2264dc)',
  },
  waiting_external: {
    label: 'Waiting on you',
    symbol: '⧗',
    color: 'var(--semantic-warning)',
    bg: 'color-mix(in oklab, var(--semantic-warning) 15%, transparent)',
  },
  pending: {
    label: 'Not started',
    symbol: '○',
    color: 'var(--text-muted)',
    bg: 'var(--surface)',
  },
  skipped: {
    label: 'Skipped',
    symbol: '–',
    color: 'var(--text-muted)',
    bg: 'var(--surface)',
  },
};

const KIND_HINT: Record<OnboardingKind, string> = {
  copilot: 'Your setup assistant handles this',
  external: 'Happens outside AuctiSync — we detect when it’s done',
  manual: 'You do this in-product',
};

/** Which branding surface a min_tier_rank gate maps to, for the upsell copy. */
const TIER_SURFACE: Record<number, string> = {
  2: 'the Fundraiser tier',
  3: 'the Starter tier',
  4: 'the Storage tier',
  5: 'the Growth tier',
  6: 'the Pro tier',
};

export function AdminOnboarding() {
  const { userId } = useSession();
  const owned = useOwnedTenants();
  const [rows, setRows] = useState<OnboardingStateRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const tenantId = owned.selectedId;
  const tenantName = owned.selected?.name ?? 'your auction house';
  const tenantSlug = owned.selected?.slug ?? null;
  const isDraft = owned.selected?.publish_state === 'draft';

  const load = useCallback(async () => {
    if (userId === null || !tenantId) return;
    setLoading(true);
    setError(null);
    const { data, error: rpcErr } = await onboardingRpc('get_onboarding_state', {
      p_tenant_id: tenantId,
    });
    if (rpcErr) {
      setError(rpcErr.message);
      setRows(null);
    } else {
      setRows(data ?? []);
    }
    setLoading(false);
  }, [userId, tenantId]);

  useEffect(() => {
    if (owned.loading) return;
    void load();
  }, [load, owned.loading]);

  // Dependency resolution + progress counts (pure logic — see onboardingChecklist.ts).
  const doneById = useMemo(() => buildDoneMap(rows ?? []), [rows]);
  const titleById = useMemo(() => buildTitleMap(rows ?? []), [rows]);
  const { completed, required } = useMemo(() => computeProgress(rows ?? []), [rows]);
  // The custom-domain external step — drives the domain-verify card below.
  const dnsStep = useMemo(
    () => (rows ?? []).find((r) => r.step_id === 'dns_custom_domain') ?? null,
    [rows],
  );

  // Go-live (publish_tenant). The RPC checks preconditions server-side; if it
  // blocks it returns the incomplete external steps.
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [blockedBy, setBlockedBy] = useState<string[] | null>(null);
  const [justPublished, setJustPublished] = useState(false);

  // Bidder materialization runs AFTER go-live: the site is already public; this
  // turns the staged bidder import into real accounts + magic-link invites in
  // the background. Batched EF, looped here until every claimed batch is empty.
  const [materializing, setMaterializing] = useState(false);
  const [bidderSetup, setBidderSetup] = useState<{
    invited: number;
    pending: number;
    exhausted: number;
  } | null>(null);
  const [materializeError, setMaterializeError] = useState<string | null>(null);
  // True when a run stopped before finishing (transient error, or exit before
  // the queue drained) — surfaces a Retry control so bidders aren't stranded.
  const [needsRetry, setNeedsRetry] = useState(false);

  // Custom-domain verification (external step dns_custom_domain, EF E4). The
  // owner points their domain at us, then triggers this on-demand check; the EF
  // confirms DNS + TLS and ticks the step complete (which unblocks go-live).
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainCheck, setDomainCheck] = useState<DomainCheck | null>(null);
  const [domainCheckError, setDomainCheckError] = useState<string | null>(null);

  // Always reflects the currently-selected tenant — used to drop a domain check
  // that resolves AFTER the owner switched houses (else the wrong house's result
  // lands in shared state).
  const selectedTenantRef = useRef(tenantId);
  useEffect(() => {
    selectedTenantRef.current = tenantId;
  }, [tenantId]);

  // Switching house clears any prior house's domain result immediately.
  useEffect(() => {
    setDomainCheck(null);
    setDomainCheckError(null);
    setCheckingDomain(false);
  }, [tenantId]);

  const checkDomain = useCallback(async () => {
    const tid = tenantId;
    if (!tid) return;
    setCheckingDomain(true);
    setDomainCheckError(null);
    const { data, error: efErr } = await invokeEF<DomainCheck>('verify-custom-domain', {
      tenant_id: tid,
    });
    // Owner switched houses mid-request → this result is stale; drop it.
    if (selectedTenantRef.current !== tid) return;
    setCheckingDomain(false);
    if (efErr) {
      setDomainCheck(null); // clear any prior not-ready/verified line
      setDomainCheckError(domainErrorCopy(efErr));
      return;
    }
    setDomainCheck(data);
    // Verified → the step flipped to complete server-side; refresh the checklist.
    if (data?.verified) void load();
  }, [tenantId, load]);

  const materializeBidders = useCallback(async (tid: string) => {
    setMaterializing(true);
    setMaterializeError(null);
    setNeedsRetry(false);
    let invited = 0;
    let completed = false;
    try {
      // Guard bounds the loop (each EF call drains one bounded batch); the EF's
      // attempts-cap guarantees a claimed batch eventually comes back empty.
      for (let guard = 0; guard < 500; guard++) {
        // Transient-retry the invoke a couple of times before giving up, so a
        // single EF gateway blip doesn't strand the rest of the bidder list.
        let res = await supabase.functions.invoke<MaterializeResponse>(
          'materialize-onboarding-bidders',
          { body: { tenant_id: tid } },
        );
        for (let attempt = 1; res.error && attempt <= 2; attempt++) {
          await new Promise((r) => setTimeout(r, 1200 * attempt));
          res = await supabase.functions.invoke<MaterializeResponse>(
            'materialize-onboarding-bidders',
            { body: { tenant_id: tid } },
          );
        }
        if (res.error) {
          setNeedsRetry(true);
          setMaterializeError(
            'We hit a network problem inviting your bidders. Tap Retry to continue.',
          );
          break;
        }
        const d = res.data;
        if (d?.error) {
          setNeedsRetry(true);
          setMaterializeError(`Bidder setup paused (${d.error}). Tap Retry to continue.`);
          break;
        }
        invited += d?.invited ?? 0;
        setBidderSetup({ invited, pending: d?.pending ?? 0, exhausted: d?.exhausted ?? 0 });
        if (d?.done) {
          completed = true;
          break;
        }
      }
      // Any non-clean exit (error break already set needsRetry, or the guard cap
      // was hit) offers Retry rather than silently reporting success.
      if (!completed) setNeedsRetry(true);
    } finally {
      setMaterializing(false);
    }
  }, []);

  const goLive = useCallback(async () => {
    if (!tenantId) return;
    setPublishing(true);
    setPublishError(null);
    setBlockedBy(null);
    const { data, error: pErr } = await publishRpc('publish_tenant', { p_tenant_id: tenantId });
    setPublishing(false);
    if (pErr) {
      setPublishError(
        /42501|owner/i.test(pErr.message) ? 'Only the owner can go live.' : pErr.message,
      );
      return;
    }
    if (data?.published === true || data?.already_live === true) {
      setJustPublished(true);
      void load();
      void materializeBidders(tenantId);
    } else if (data?.blocked_by) {
      setBlockedBy(data.blocked_by);
    }
  }, [tenantId, load, materializeBidders]);

  if (owned.loading || loading) {
    return <div style={wrapStyle}>Loading your setup checklist…</div>;
  }

  if (!tenantId) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: 'var(--text-muted)' }}>
          The setup checklist is available to tenant owners.
        </p>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <header style={{ marginBottom: '1.25rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Set up {tenantName}
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.3rem', maxWidth: '42rem' }}>
          Your setup assistant walks you through each step. Nothing here is public until you review
          your finished site and choose to go live — configure freely.
        </p>
      </header>

      <HouseSelector
        tenants={owned.tenants}
        selectedId={owned.selectedId}
        onSelect={owned.setSelectedId}
        disabled={false}
      />

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {!isDraft ? (
        <section style={cardStyle}>
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
            {tenantName} is live 🎉
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: '0.4rem 0 0' }}>
            Setup is complete — your site is published. There’s nothing left to do here.
          </p>
        </section>
      ) : (
        <>
          <ProgressBar completed={completed} required={required} />

          <p style={{ margin: '0.6rem 0 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
            Bringing bidders from another system?{' '}
            <Link
              to="/admin/onboarding/bidders"
              style={{ color: 'var(--brand-link)', fontWeight: 500 }}
            >
              Import your list →
            </Link>
          </p>

          <ol
            style={{
              listStyle: 'none',
              margin: '1rem 0 0',
              padding: 0,
              display: 'grid',
              gap: '0.6rem',
            }}
          >
            {(rows ?? []).map((r) => {
              const unmet = unmetDependencies(r, doneById);
              return (
                <StepRow
                  key={r.step_id}
                  row={r}
                  locked={isStepLocked(r, doneById)}
                  unmetDepTitles={unmet.map((d) => titleById.get(d) ?? d)}
                />
              );
            })}
          </ol>

          {dnsStep && dnsStep.entitled && !isStepDone(dnsStep.status) && (
            <section style={{ ...cardStyle, marginTop: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
                Your custom domain
              </h2>
              <p
                style={{
                  color: 'var(--text-muted)',
                  margin: '0.4rem 0 0.75rem',
                  fontSize: '0.9rem',
                }}
              >
                {typeof dnsStep.detail?.custom_domain === 'string' ? (
                  <>
                    Point <strong>{dnsStep.detail.custom_domain}</strong> at AuctiSync, then check
                    it here. We confirm your DNS and the secure certificate before you can go live.
                  </>
                ) : (
                  <>Choose your custom domain with the setup assistant, then verify it here.</>
                )}
              </p>
              {domainCheckError && (
                <p role="alert" style={errorStyle}>
                  {domainCheckError}
                </p>
              )}
              {domainCheck && !domainCheck.verified && (
                <p
                  role="status"
                  style={{
                    margin: '0 0 0.75rem',
                    fontSize: '0.85rem',
                    color: 'var(--semantic-warning)',
                  }}
                >
                  {domainNotReadyCopy(domainCheck)}
                </p>
              )}
              {domainCheck?.verified && (
                <p
                  role="status"
                  style={{
                    margin: '0 0 0.75rem',
                    fontSize: '0.85rem',
                    color: 'var(--semantic-success)',
                    fontWeight: 600,
                  }}
                >
                  ✓ Verified — {domainCheck.domain} is pointed at us and secure. You’re clear to go
                  live.
                </p>
              )}
              <Button onClick={() => void checkDomain()} disabled={checkingDomain}>
                {checkingDomain ? 'Checking…' : 'Check my domain'}
              </Button>
            </section>
          )}

          <section style={{ ...cardStyle, marginTop: '1.25rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
              Go live
            </h2>
            {justPublished ? (
              <>
                <p
                  style={{
                    color: 'var(--semantic-success)',
                    margin: '0.4rem 0 0',
                    fontWeight: 600,
                  }}
                >
                  🎉 You’re live! Your site is now public.
                </p>
                {materializing && (
                  <p
                    style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0', fontSize: '0.9rem' }}
                  >
                    Inviting your bidders…
                    {bidderSetup && bidderSetup.invited > 0
                      ? ` (${bidderSetup.invited} so far)`
                      : ''}
                  </p>
                )}
                {!materializing && bidderSetup && (
                  <p
                    style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0', fontSize: '0.9rem' }}
                  >
                    {bidderSetup.invited > 0
                      ? `We emailed ${bidderSetup.invited} bidder${
                          bidderSetup.invited === 1 ? '' : 's'
                        } an invite to sign in.`
                      : 'Your bidders are set up.'}
                    {bidderSetup.pending > 0
                      ? ` ${bidderSetup.pending} still to go.`
                      : bidderSetup.exhausted > 0
                        ? ` ${bidderSetup.exhausted} couldn’t be set up automatically — the assistant can help.`
                        : ''}
                  </p>
                )}
                {materializeError && (
                  <p
                    role="alert"
                    style={{
                      color: 'var(--semantic-warning)',
                      margin: '0.5rem 0 0',
                      fontSize: '0.85rem',
                    }}
                  >
                    {materializeError}
                  </p>
                )}
                {!materializing && needsRetry && tenantId && (
                  <div style={{ marginTop: '0.6rem' }}>
                    <Button onClick={() => void materializeBidders(tenantId)}>
                      Retry inviting bidders
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p
                  style={{
                    color: 'var(--text-muted)',
                    margin: '0.4rem 0 0.75rem',
                    fontSize: '0.9rem',
                  }}
                >
                  When you’re ready, publish your site. We’ll check that payouts (and your domain,
                  if custom) are set up first — nothing goes public until you click this.
                </p>
                {tenantSlug && (
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
                    <a
                      href={`/preview/${tenantSlug}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--brand-link)', fontWeight: 600 }}
                    >
                      Preview your site ↗
                    </a>{' '}
                    <span style={{ color: 'var(--text-muted)' }}>
                      — see exactly how bidders will see it (opens in a new tab).
                    </span>
                  </p>
                )}
                {blockedBy && blockedBy.length > 0 && (
                  <p
                    role="alert"
                    style={{
                      margin: '0 0 0.75rem',
                      fontSize: '0.85rem',
                      color: 'var(--semantic-warning)',
                    }}
                  >
                    Finish these first: {blockedBy.map((id) => titleById.get(id) ?? id).join(', ')}.
                  </p>
                )}
                {publishError && (
                  <p role="alert" style={errorStyle}>
                    {publishError}
                  </p>
                )}
                <Button onClick={() => void goLive()} disabled={publishing}>
                  {publishing ? 'Publishing…' : 'Go live'}
                </Button>
              </>
            )}
          </section>

          <div
            style={{
              ...cardStyle,
              marginTop: '1.25rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: '16rem' }}>
              <strong style={{ color: 'var(--text)' }}>Need a hand?</strong>
              <p style={{ color: 'var(--text-muted)', margin: '0.2rem 0 0', fontSize: '0.9rem' }}>
                Your setup assistant can do most of this for you — just ask.
              </p>
            </div>
            <Link to="/admin/copilot" style={{ textDecoration: 'none' }}>
              <Button>Open the assistant</Button>
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

function ProgressBar({ completed, required }: { completed: number; required: number }) {
  const pct = required === 0 ? 0 : Math.round((completed / required) * 100);
  return (
    <div style={cardStyle}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: '0.5rem',
        }}
      >
        <span style={{ fontWeight: 600, color: 'var(--text)' }}>
          {completed} of {required} steps complete
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{pct}%</span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          height: '0.5rem',
          borderRadius: '999px',
          background: 'var(--surface)',
          border: '1px solid var(--admin-hairline, var(--border))',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--brand-primary, #0a0045)',
            transition: 'width 240ms ease',
          }}
        />
      </div>
    </div>
  );
}

function StepRow({
  row,
  locked,
  unmetDepTitles,
}: {
  row: OnboardingStateRow;
  locked: boolean;
  unmetDepTitles: string[];
}) {
  const meta = STATUS_META[row.status];
  const dimmed = !row.entitled || locked;
  const surface =
    row.min_tier_rank != null ? (TIER_SURFACE[row.min_tier_rank] ?? 'a higher tier') : '';

  return (
    <li
      style={{
        ...cardStyle,
        marginBottom: 0,
        display: 'flex',
        gap: '0.85rem',
        alignItems: 'flex-start',
        opacity: dimmed ? 0.6 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          flexShrink: 0,
          width: '1.75rem',
          height: '1.75rem',
          borderRadius: '999px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.95rem',
          fontWeight: 700,
          color: meta.color,
          background: meta.bg,
          border: '1px solid var(--admin-hairline, var(--border))',
        }}
      >
        {meta.symbol}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.title}</span>
          <StatusBadge status={row.status} label={meta.label} />
          {row.kind === 'external' && <Badge variant="outline">External</Badge>}
        </div>
        {row.description && (
          <p style={{ color: 'var(--text-muted)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
            {row.description}
          </p>
        )}
        <p
          style={{
            color: 'var(--text-subtle, var(--text-muted))',
            margin: '0.35rem 0 0',
            fontSize: '0.8rem',
          }}
        >
          {KIND_HINT[row.kind]}
        </p>
        {!row.entitled && (
          <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
            🔒 Unlocks with {surface}.
          </p>
        )}
        {row.entitled && locked && (
          <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 0', fontSize: '0.8rem' }}>
            Complete {unmetDepTitles.join(' and ')} first.
          </p>
        )}
      </div>
    </li>
  );
}

function StatusBadge({ status, label }: { status: OnboardingStatus; label: string }) {
  const variant =
    status === 'complete'
      ? ('success' as const)
      : status === 'waiting_external'
        ? ('warning' as const)
        : status === 'in_progress'
          ? ('default' as const)
          : ('secondary' as const);
  return <Badge variant={variant}>{label}</Badge>;
}

const wrapStyle: React.CSSProperties = {
  padding: '2rem 2.25rem',
  maxWidth: '52rem',
};

const cardStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  background: 'var(--surface-elev)',
  marginBottom: '1rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--surface-danger, #fef2f2)',
  color: 'var(--text-danger, #991b1b)',
  border: '1px solid var(--border-danger, #fecaca)',
  marginBottom: '1rem',
};
